import React from "react";
import { CONFIG } from "../config";
import { ShieldAlert, ArrowRight } from "lucide-react";
import { ProductImage } from "./ProductImage";

export default function HeroSection() {
  return (
    <section className="bg-white pt-6 pb-16 md:pt-16 md:pb-20 border-b border-brand-border">
      <div className="max-w-[1120px] mx-auto px-4 md:px-6">
        
        {/* Eyebrow */}
        <div className="text-center mb-6" data-testid="hero-eyebrow-badge">
          <span className="inline-flex items-center gap-1.5 bg-brand-red/10 border border-brand-red/20 text-brand-red text-xs md:text-sm font-extrabold uppercase px-3 py-1 rounded-full tracking-widest">
            <ShieldAlert className="w-4 h-4" />
            {CONFIG.title.split("|")[1] || "DEBT COLLECTOR KILL SWITCH"} IS BACK
          </span>
        </div>

        {/* H1 Main Headline */}
        <h1 className="text-center font-display font-black uppercase text-brand-dark tracking-tight leading-none mb-6 max-w-[1000px] mx-auto text-3xl sm:text-5xl lg:text-[64px]">
          DEBT COLLECTORS HOPE YOU NEVER GET THE{" "}
          <span className="text-brand-red block sm:inline">KILL SWITCH!</span>
        </h1>

        {/* H2 Main Subheadline with Highlighting */}
        <p className="text-center font-display font-bold text-brand-dark/90 leading-snug max-w-[900px] mx-auto mb-4 text-xl sm:text-2xl lg:text-[28px]">
          DEMAND PROOF OF THEIR LEGAL AUTHORITY TO COLLECT - THEN{" "}
          <span className="marker-highlight inline">
            WATCH HOW FAST
          </span> THEY FOLD!
        </p>

        {/* Short Line */}
        <p className="text-center text-brand-red font-sans font-extrabold uppercase tracking-wider mb-10 text-lg md:text-xl sm:whitespace-nowrap">
          They want the full balance. The Kill Switch demands the full proof.
        </p>

        {/* Desktop Grid Layout: Left (62%) / Right (38%) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
          
          {/* LEFT COLUMN: Main Product Image (62%) */}
          <div className="lg:col-span-7 flex flex-col">
            <h3 className="font-display font-extrabold text-xs text-brand-dark/60 uppercase tracking-widest mb-2 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-brand-red inline-block"></span>
              STOP! NEVER PAY A DEBT COLLECTOR! ACTIVATE THE KILL SWITCH TODAY!
            </h3>

            <div className="bg-card rounded-xl border-4 border-brand-dark shadow-2xl overflow-hidden flex-1 min-h-[300px] flex items-center">
              <ProductImage
                imageKey="heroLeft"
                alt="Stop! Never pay a debt collector! Make them prove they have the legal right to collect - Debt Collector Kill Switch"
                sizes="(min-width: 1024px) 62vw, 92vw"
                loading="eager"
                fetchPriority="high"
                testId="hero-main-product-image"
              />
            </div>

            <p className="text-brand-dark/70 text-xs md:text-sm mt-3 leading-relaxed italic max-w-[760px]">
              The Debt Collector Kill Switch gives you the complete collection removal attack system — ready to use without hiring an attorney or paying a credit repair company.
            </p>
          </div>

          {/* RIGHT COLUMN: Product Image & CTA Container (38%) */}
          <div className="lg:col-span-5 flex flex-col justify-between bg-card border-4 border-brand-dark p-6 rounded-xl shadow-xl relative overflow-hidden">
            {/* Custom 3D-effect shadow backdrop */}
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-brand-blue/10 rounded-full blur-3xl pointer-events-none"></div>
            
            <div className="relative z-10 flex flex-col h-full">
              
              <div className="mb-6 overflow-hidden rounded-lg bg-brand-cream/20 p-2 sm:p-3">
                <ProductImage
                  imageKey="main"
                  alt="Choose your debt collection situation and matching attack file"
                  sizes="(min-width: 1024px) 36vw, (min-width: 640px) 70vw, 92vw"
                  loading="eager"
                  fetchPriority="high"
                  testId="hero-product-image"
                  className="rounded-md"
                />
              </div>

              {/* Price Block */}
              <div className="bg-brand-cream/50 rounded-lg p-4 border-2 border-dashed border-brand-border text-center mb-6" data-testid="hero-price-block">
                <div className="flex items-center justify-center gap-3 mb-1">
                  <span className="text-brand-dark line-through decoration-brand-red decoration-2 font-display font-bold text-lg md:text-xl">
                    NORMAL PRICE: ${CONFIG.regularPrice}
                  </span>
                  <span className="bg-[#16A34A] text-white text-[10px] font-extrabold uppercase px-2 py-0.5 rounded tracking-wider">
                    SAVE 76%
                  </span>
                </div>
                <div className="text-3xl md:text-4xl lg:text-[44px] font-display font-black text-brand-red leading-none">
                  KILL SWITCH<br />ACCESS: ${CONFIG.currentPrice}
                </div>
                <span className="text-[9px] sm:text-[10px] uppercase text-brand-dark font-extrabold tracking-wide mt-1.5 block whitespace-nowrap">
                  25+ CUSTOM ATTACK FILES • $2,057 IN TOTAL VALUE!
                </span>
              </div>

              {/* CTA Button */}
              <div className="mt-auto">
                <a
                  href={CONFIG.checkoutUrl}
                  id="hero-cta"
                  className="w-full py-4 px-6 bg-brand-cta hover:bg-brand-cta-hover text-white rounded-lg font-sans font-extrabold uppercase text-center text-base md:text-lg tracking-wide flex items-center justify-center gap-2 border-b-4 border-brand-dark/30 hover:border-b-2 active:border-b-0 hover:translate-y-[2px] active:translate-y-[4px] transition-all duration-150 animate-pulse-glow hover:animate-none cursor-pointer"
                >
                  <span className="whitespace-nowrap">YES! REMOVE COLLECTIONS</span>
                  <ArrowRight className="w-7 h-7" />
                </a>

                {/* Microcopies */}
                <p className="text-center text-brand-dark/70 font-sans font-semibold text-[11px] sm:text-xs mt-3 whitespace-nowrap">
                  🔒 Secure checkout ⚡ Instant Private Access
                </p>
                <p className="text-center text-brand-red font-sans font-bold text-xs mt-1">
                  START REMOVING YOUR COLLECTIONS TODAY!
                </p>
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
